import { useState, useRef, useEffect } from "react";
import { useConfirm } from "../ui-components/ConfirmDialog";
import { Tabs, TabList, Tab, TabPanel } from "react-tabs";
import { shallow } from "zustand/shallow";
import useTabStore from "../stores/useTabStore";
import TabContent from "./TabContent";
import MobileTabDropdown from "./MobileTabDropdown";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { WidgetdropdownMenu } from "../ui-components/WidgetsDropdownMenu";
import { Popover, PopoverButton } from "@headlessui/react";
import { Button } from "../ui-components/button-with-loading";
import { destroyPersistentState } from "../hooks/useStateWithOptionalPersistence";
import { trackFeatureUsage } from "../utils/gtm";

const PREDEFINED_TAB_IDS = ["default", "BrokerView"];

const TabSystem = () => {
  const confirm = useConfirm();
  const { tabs, activeTabId, switchTab, addTab, removeTab, renameTab } =
    useTabStore(
      (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        switchTab: state.switchTab,
        addTab: state.addTab,
        removeTab: state.removeTab,
        renameTab: state.renameTab,
      }),
      shallow
    );

  const [editingTabId, setEditingTabId] = useState(null);
  const editRef = useRef(null);

  useEffect(() => {
    if (editingTabId && editRef.current) {
      const el = editRef.current;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editingTabId]);

  const handleStartRename = (tab) => {
    if (PREDEFINED_TAB_IDS.includes(tab.id)) return;
    setEditingTabId(tab.id);
  };

  const handleFinishRename = () => {
    if (editingTabId && editRef.current) {
      const newTitle = editRef.current.textContent.trim();
      if (newTitle) {
        renameTab(editingTabId, newTitle);
      }
    }
    setEditingTabId(null);
  };

  const handleCancelRename = (originalTitle) => {
    if (editRef.current) {
      editRef.current.textContent = originalTitle;
    }
    setEditingTabId(null);
  };

  const selectedIndex = tabs.findIndex((tab) => tab.id === activeTabId);

  const handleAddTab = () => {
    const title = prompt("Enter new tab title");
    if (title) {
      addTab(title);
    }
  };

  const handleRemoveTab = async (e, tabId) => {
    e.stopPropagation();
    e.preventDefault();
    const ok = await confirm({ title: "Delete Tab", message: "This tab and all its widget configurations will be permanently deleted. This action cannot be undone.", confirmLabel: "Delete", variant: "danger" });
    if (ok) {
      const tab = tabs.find((tab) => tab.id === tabId);
      if (tab) {
        tab.enabledWidgets.forEach((widgetCompositeId) => {
          destroyPersistentState(widgetCompositeId);
          destroyPersistentState(`tvAdvancedChart_lastSymbol_${widgetCompositeId}`);
          destroyPersistentState(`broker:symbol:${widgetCompositeId}`);
          destroyPersistentState(`broker:auto-switch:${widgetCompositeId}`);
        });
        removeTab(tab.id);
      }
    }
  };

  return (
    <>
      <Tabs
        selectedIndex={selectedIndex === -1 ? 0 : selectedIndex}
        onSelect={(index) => {
          switchTab(tabs[index].id);
          trackFeatureUsage({
            feature_name: "dashboard_tab",
            feature_action: "switch",
            feature_detail: tabs[index].title,
          });
        }}
        className={"relative"}
      >
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700/50 z-40 sticky top-0 py-0.5 sm:py-0 px-2 mb-0.5 sm:mb-0 text-1.5xs transition-colors">
          <TabList className="hidden sm:flex">
            {tabs.map((tab) => {
              const isActive = tab.id === activeTabId;
              const isDefault = tab.id === "default";
              return (
                <Tab
                  key={tab.id}
                  className={`inline-block rounded-b-none rounded-t-md border-transparent list-none px-3 cursor-pointer focus:outline-none pt-0.5 transition-all duration-150 ${
                    isActive
                      ? "bg-white dark:bg-gray-900 border-b-2 border-squawk-blue text-gray-800 dark:text-white font-semibold rounded-t-md"
                      : "bg-transparent text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  <div className="flex items-center">
                    <span
                      ref={editingTabId === tab.id ? editRef : undefined}
                      className={`capitalize outline-none ${
                        editingTabId === tab.id
                          ? "animate-pulse border-b-2 border-blue-500 pb-px"
                          : ""
                      }`}
                      contentEditable={editingTabId === tab.id}
                      suppressContentEditableWarning
                      onDoubleClick={() => handleStartRename(tab)}
                      onBlur={() => {
                        if (editingTabId === tab.id) handleFinishRename();
                      }}
                      onKeyDown={(e) => {
                        if (editingTabId !== tab.id) return;
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.target.blur();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          handleCancelRename(tab.title);
                        }
                      }}
                      onClick={(e) => {
                        if (editingTabId === tab.id) e.stopPropagation();
                      }}
                    >
                      {tab.title}
                    </span>
                    {/* Always render the button to maintain consistent layout */}
                    <button
                      onClick={(e) => {
                        // Only call remove if not the default tab
                        if (!isDefault) handleRemoveTab(e, tab.id);
                      }}
                      className={`bg-transparent py-0 my-0 ml-2 mr-0 px-2 text-red-500 hover:text-red-700 hover:outline-none
                      ${
                        isDefault || !isActive
                          ? "invisible pointer-events-none px-0 ml-0"
                          : "visible"
                      }`}
                      aria-label={`Remove ${tab.title} tab`}
                      disabled={isDefault}
                    >
                      &times;
                    </button>
                  </div>
                </Tab>
              );
            })}
            <div className="ml-2 flex">
              <Button onClick={handleAddTab} variant="secondary" size="xs">
                + Add Tab
              </Button>
            </div>
          </TabList>

          {/* Tabs Mobile Dropdown */}
          <div className="bg-transparent py-0 flex items-center">
            <div className="flex sm:hidden">
              <MobileTabDropdown />
            </div>
          </div>

          {/* ManageWidget Dropdown */}
          <div className="ml-auto ">
            <Popover>
              <PopoverButton
                as={Button}
                size="xs"
                variant="secondary"
                renderIcon={() => (
                  <AdjustmentsHorizontalIcon className="h-4 w-4" />
                )}
              >
                Manage Widgets
              </PopoverButton>

              <WidgetdropdownMenu />
            </Popover>

            {/* <SimpleDropdown /> */}
          </div>
        </div>

        {tabs.map((tab) => (
          <TabPanel key={tab.id}>
            <TabContent tab={tab} />
          </TabPanel>
        ))}
      </Tabs>
    </>
  );
};

export default TabSystem;
